import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const isRTL = i18n.language === 'fa';

  const privacySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/privacy#webpage`,
        url: `${siteUrl}/privacy`,
        name: 'Privacy Policy - Loveish Group',
        description: 'Read our privacy policy to understand how we collect, use, and protect your personal information.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        breadcrumb: {
          '@id': `${siteUrl}/privacy#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/privacy#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Privacy Policy',
            item: `${siteUrl}/privacy`
          }
        ]
      }
    ]
  };

  useSEO({
    title: 'Privacy Policy - Loveish Group',
    description: 'Read our privacy policy to understand how we collect, use, and protect your personal information.',
    keywords: 'privacy policy, data protection, personal information',
    canonical: `${siteUrl}/privacy`,
    schema: privacySchema
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 md:pt-24">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Privacy Policy
            </h1>
            <p className="text-base md:text-lg text-gray-600 text-center">
              Last updated: January 2024
            </p>
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className={`prose prose-lg max-w-none ${isRTL ? 'text-right' : 'text-left'}`}>
            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                At Loveish, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase from us.
              </p>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3">Personal Information</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-600 mb-6">
                <li>Name and contact information (email, phone number, shipping address)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Order history and preferences</li>
                <li>Account credentials if you create an account</li>
              </ul>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3">Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-600">
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>IP address and location data</li>
                <li>Pages visited and time spent on our site</li>
              </ul>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-600">
                <li>Process and fulfill your orders</li>
                <li>Communicate with you about your orders and account</li>
                <li>Send you marketing communications (with your consent)</li>
                <li>Improve our website and customer service</li>
                <li>Prevent fraud and enhance security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Data Security</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All payment transactions are processed through Stripe's secure payment gateway, and we do not store your credit card information on our servers.
              </p>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Sharing Your Information</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                We do not sell or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-600">
                <li>Service providers who assist us in operating our website and conducting our business</li>
                <li>Payment processors (Stripe) to process transactions</li>
                <li>Shipping companies to deliver your orders</li>
                <li>Law enforcement or regulatory authorities when required by law</li>
              </ul>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-600">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your personal information</li>
                <li>Withdraw consent for marketing communications</li>
              </ul>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Cookies</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand where our visitors are coming from. You can control cookies through your browser settings.
              </p>
            </section>

            <section className="mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-base md:text-lg text-gray-700 mb-2"><strong>Email:</strong> privacy@loveish.com</p>
                <p className="text-base md:text-lg text-gray-700 mb-2"><strong>Phone:</strong> +374 93 573467</p>
                <p className="text-base md:text-lg text-gray-700"><strong>Address:</strong> Tehran, Iran</p>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}